import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, NgZone, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { AgentCatalogLoadingIndicator, getAgentEntry, getAgentName } from 'src/app/data/agent-catalog';
@Component({
  selector: 'app-thread-status',
  templateUrl: './thread-status.component.html',
  styleUrl: './thread-status.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThreadStatusComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentThread: any | null = null;
  @Input() loading: boolean = false;
  @Input() agent: string | null = null; // Agent type abbreviation (sdr, csm, mm, etc.)

  // Make Math available in template
  Math = Math;

  private currentSassyIndicators: AgentCatalogLoadingIndicator[] = [];
  
  // Sassy loading indicators animation
  private sassyAnimationInterval?: any;
  private currentSassyIndex: number = 0;
  private currentSassyText: string = '';
  private isTyping: boolean = true;
  private typingIndex: number = 0;

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) { }

  get hasActiveExecution(): boolean {
    if (this.loading) {
      return true;
    }

    return this.currentThread?.status === 'continuing'
      || this.currentThread?.status === 'running';
  }

  ngOnInit(): void {
    // Fetch system prompt when component initializes (only if agent is provided)
    if (this.agent) {
      this.fetchSystemPrompt();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If agent type changes, fetch new system prompt (only if agent has a value)
    if (changes['agent']) {
      if (this.agent) {
        this.fetchSystemPrompt();
      } else {
        // If agent becomes null, clear metadata
        this.currentSassyIndicators = [];
        this.stopSassyAnimation();
      }
    }
    
    // If thread or loading state changes, update sassy animation
    if (changes['currentThread'] || changes['loading']) {
      this.updateSassyAnimation();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions and animations
    if (this.sassyAnimationInterval) {
      clearInterval(this.sassyAnimationInterval);
    }
  }

  /**
   * Get the status display text based on thread status and loading state
   */
  get statusText(): string {
    if (this.loading && this.currentThread?.status === 'waiting_for_user') {
      return 'Preparing response';
    }

    if (this.loading) {
      return 'Working on it...';
    }
    
    if (!this.currentThread) {
      return '';
    }

    // Show nicely formatted status messages
    switch (this.currentThread.status) {
      case 'continuing':
        return 'Agent is working';
      case 'running':
        return 'Processing request';
      case 'waiting_for_user':
        return 'Ready for your input';
      case 'complete':
        return 'Task completed';
      case 'error':
        return 'Needs attention';
      case 'stopped':
        return 'Stopped';
      default:
        return this.currentThread.status;
    }
  }

  get statusStateClass(): string {
    if (this.loading) {
      return 'thread-status--loading';
    }

    switch (this.currentThread?.status) {
      case 'complete':
        return 'thread-status--complete';
      case 'error':
        return 'thread-status--error';
      case 'waiting_for_user':
        return 'thread-status--waiting';
      case 'stopped':
        return 'thread-status--stopped';
      case 'running':
      case 'continuing':
        return 'thread-status--active';
      default:
        return 'thread-status--idle';
    }
  }

  /**
   * Get the status icon based on thread status
   */
  get statusIcon(): string {
    if (this.hasActiveExecution) {
      return '';
    }

    if (!this.currentThread) {
      return '';
    }

    switch (this.currentThread.status) {
      case 'continuing':
        return 'fas fa-cogs';
      case 'running':
        return 'fas fa-circle-notch fa-spin';
      case 'waiting_for_user':
        return 'fas fa-clock';
      case 'complete':
        return 'fas fa-check-circle';
      case 'error':
        return 'fas fa-exclamation-triangle';
      case 'stopped':
        return 'fas fa-stop-circle';
      default:
        return 'fas fa-info-circle';
    }
  }

  /**
   * Calculate overall progress percentage (task completion)
   */
  get overallProgress(): number {
    if (!this.currentThread?.estimatedTotalIterations) {
      return 0;
    }

    const remaining = this.currentThread.estimatedRemainingIterations || 0;
    const total = this.currentThread.estimatedTotalIterations;
    const completed = Math.max(0, total - remaining);
    const progress = (completed / total) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  /**
   * Calculate intervention progress percentage (time until next intervention)
   */
  get interventionProgress(): number {
    if (!this.currentThread?.estimatedTotalIterationsUntilIntervention) {
      return 0;
    }

    const remaining = this.currentThread.estimatedRemainingIterationsUntilIntervention || 0;
    const total = this.currentThread.estimatedTotalIterationsUntilIntervention;
    const completed = Math.max(0, total - remaining);
    const progress = (completed / total) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  /**
   * Check if we should show progress bars
   * Always show progress if we have the data
   */
  get shouldShowProgress(): boolean {
    if (!this.currentThread) {
      return false;
    }
    
    // Show progress if we have either type of progress data
    return !!(this.currentThread.estimatedTotalIterations || 
             this.currentThread.estimatedTotalIterationsUntilIntervention);
  }

  /**
   * Get the agent type display name
   */
  get agentDisplayName(): string {
    const agentType = this.currentThread?.agentName || this.currentThread?.agentType;
    if (!agentType) {
      return '';
    }

    return getAgentName(agentType, agentType);
  }

  /**
   * Check if the component should be visible
   * Always show when there's a thread or when loading
   */
  get isVisible(): boolean {
    return this.loading || !!this.currentThread;
  }

  /**
   * Get the status color based on thread status
   */
  get statusColor(): string {
    if (this.loading) {
      return 'var(--thread-status-loading-color)';
    }

    if (!this.currentThread) {
      return 'var(--thread-status-idle-color)';
    }

    switch (this.currentThread.status) {
      case 'continuing':
        return 'var(--thread-status-active-color)';
      case 'running':
        return 'var(--thread-status-running-color)';
      case 'waiting_for_user':
        return 'var(--thread-status-waiting-color)';
      case 'complete':
        return 'var(--thread-status-complete-color)';
      case 'error':
        return 'var(--thread-status-error-color)';
      case 'stopped':
        return 'var(--thread-status-stopped-color)';
      default:
        return 'var(--thread-status-idle-color)';
    }
  }

  /**
   * Check if we should show sassy loading indicators
   * Only show during continuing and running states, and if we have indicators
   */
  get shouldShowSassyIndicators(): boolean {
    if (this.currentSassyIndicators.length === 0) {
      return false;
    }

    if (!this.currentThread) {
      return false;
    }

    // Only show during active processing states
    return this.currentThread.status === 'continuing' || 
           this.currentThread.status === 'running';
  }

  get shouldShowSubtleLoader(): boolean {
    return this.hasActiveExecution;
  }

  /**
   * Get the current sassy text being displayed
   */
  get currentSassyDisplayText(): string {
    return this.currentSassyText;
  }

  /**
   * Fetch the system prompt for the current agent type
   */
  private fetchSystemPrompt(): void {
    if (!this.agent) {
      this.currentSassyIndicators = [];
      return;
    }

    this.currentSassyIndicators = getAgentEntry(this.agent)?.sassyLoadingIndicators || [];
    this.updateSassyAnimation();
    this.cdr.markForCheck();
  }

  /**
   * Update sassy animation based on current state
   */
  private updateSassyAnimation(): void {
    if (this.shouldShowSassyIndicators) {
      this.startSassyAnimation();
    } else {
      this.stopSassyAnimation();
    }
  }

  /**
   * Start the sassy loading indicators animation
   */
  private startSassyAnimation(): void {
    if (this.currentSassyIndicators.length === 0) {
      return;
    }

    // Stop existing animation
    this.stopSassyAnimation();

    // Reset animation state
    this.currentSassyIndex = 0;
    this.currentSassyText = '';
    this.isTyping = true;
    this.typingIndex = 0;

    // Start animation loop outside Angular zone to avoid triggering change detection every 80ms
    this.ngZone.runOutsideAngular(() => {
      this.sassyAnimationInterval = setInterval(() => {
        const prevText = this.currentSassyText;
        this.animateSassyText();
        if (this.currentSassyText !== prevText) {
          this.cdr.detectChanges();
        }
      }, 80);
    });
  }

  /**
   * Stop the sassy loading indicators animation
   */
  private stopSassyAnimation(): void {
    if (this.sassyAnimationInterval) {
      clearInterval(this.sassyAnimationInterval);
      this.sassyAnimationInterval = undefined;
    }
    this.currentSassyText = '';
  }

  /**
   * Animate the typing and backspacing of sassy text
   */
  private animateSassyText(): void {
    if (this.currentSassyIndicators.length === 0) {
      return;
    }

    const indicators = this.currentSassyIndicators;
    const currentIndicator = indicators[this.currentSassyIndex];
    if (!currentIndicator) {
      return;
    }
    const targetText = currentIndicator.description || currentIndicator.name;

    if (this.isTyping) {
      // Typing phase
      if (this.typingIndex < targetText.length) {
        this.currentSassyText = targetText.substring(0, this.typingIndex + 1);
        this.typingIndex++;
      } else {
        // Pause at full text for a moment, then start backspacing
        setTimeout(() => {
          this.isTyping = false;
        }, 1500); // Pause for 1.5 seconds at full text
      }
    } else {
      // Backspacing phase
      if (this.currentSassyText.length > 0) {
        this.currentSassyText = this.currentSassyText.substring(0, this.currentSassyText.length - 1);
      } else {
        // Move to next indicator
        this.currentSassyIndex = (this.currentSassyIndex + 1) % indicators.length;
        this.isTyping = true;
        this.typingIndex = 0;
        
        // Brief pause before starting next text
        setTimeout(() => {
          // Animation will continue in next interval
        }, 300);
      }
    }
  }
}
